import { c as _c } from "react-compiler-runtime";
import { useCallback, useMemo } from 'react';
import { useCrmObjectType } from 'framework-listing-lib/internal/providers/CrmObjectTypeListingClientProvider';
import { getInitialFiltersStateForUIMode, removePropertyFilters, updateFilterState } from 'framework-listing-lib/utils/filters';
import useExpandCollapseRow from 'framework-listing-lib/hooks/useExpandCollapseRow';
import { UI_MODE } from 'framework-listing-lib/constants/uiMode';
import { useHasViews } from '../internal/ViewTabs/hooks/useViewProps';
import useUIMode from 'framework-listing-lib/internal/hooks/useUIMode';
import useCurrentView, { useCurrentViewFilters } from 'framework-listing-lib/hooks/useCurrentView';
/**
 * This hook reads the global filter from CrmObjectTypeListingClientProvider and provides two
 * main methods to manipulate filters in the codebase:
 *  - onUpdateFilters: to update/override the filter state.
 *  - onDeleteObject: to delete & ignore objectIds from upcoming requests to crm-search.
 *
 * It also propagates current applied filters as well as list of deleted objects.
 *
 * Components & hooks that need to read/write filter state should pull the filter information from this hook (useCrmObjectTypeListingFilters).
 */
function useCrmObjectTypeListingFilters() {
  "use memo";

  const $ = _c(34);
  const uiModeProps = useUIMode();
  const {
    deletedObjects,
    filters,
    hiddenFilter,
    isFilterFromCustomView,
    initialFilter,
    onSetDeletedObjects,
    onSetFilters
  } = useCrmObjectType();
  const hasViews = useHasViews();
  const currentView = useCurrentView();
  const currentViewFilters = useCurrentViewFilters();
  const {
    collapseAll
  } = useExpandCollapseRow();
  const currentQuickFilters = filters.quickFilters;
  let t0;
  if ($[0] !== currentQuickFilters.length || $[1] !== filters.filterGroups) {
    t0 = filters.filterGroups.some(_temp) || currentQuickFilters.length > 0;
    $[0] = currentQuickFilters.length;
    $[1] = filters.filterGroups;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const hasAppliedFilters = t0;
  let t1;
  if ($[3] !== collapseAll || $[4] !== onSetFilters) {
    t1 = propertyName => {
      onSetFilters(prevFilters => Object.assign({}, prevFilters, {
        filterGroups: removePropertyFilters(prevFilters.filterGroups, propertyName)
      }));
      collapseAll();
    };
    $[3] = collapseAll;
    $[4] = onSetFilters;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const onRemovePropertyFilter = t1;
  let t2;
  if ($[6] !== collapseAll || $[7] !== onSetFilters) {
    t2 = (updatedFilter, internalOptions) => {
      onSetFilters(prevFilters_0 => updateFilterState(prevFilters_0, updatedFilter), internalOptions);
      collapseAll();
    };
    $[6] = collapseAll;
    $[7] = onSetFilters;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const onUpdateFilters = t2;
  let t3;
  if ($[9] !== currentView || $[10] !== currentViewFilters.filterGroups || $[11] !== currentViewFilters.quickFilters || $[12] !== hasViews || $[13] !== initialFilter || $[14] !== onSetFilters || $[15] !== onUpdateFilters || $[16] !== uiModeProps.currentUIMode) {
    t3 = t4 => {
      const {
        newUIMode
      } = t4 === undefined ? {} : t4;
      const isGoingToFoldersMode = Boolean(newUIMode && newUIMode !== uiModeProps.currentUIMode && newUIMode === UI_MODE.FOLDERS);
      if (isGoingToFoldersMode) {
        onSetFilters(getInitialFiltersStateForUIMode({
          initialFilter,
          uiMode: UI_MODE.FOLDERS
        }));
        return;
      }
      const initialFilterGroups = initialFilter && initialFilter.filterGroups ? initialFilter.filterGroups : [];
      const initialQuickFilters = initialFilter && initialFilter.quickFilters ? initialFilter.quickFilters : [];
      if (hasViews && currentView) {
        onUpdateFilters({
          filterGroups: currentViewFilters.filterGroups,
          quickFilters: currentViewFilters.quickFilters,
          query: ""
        });
      } else {
        onUpdateFilters({
          filterGroups: initialFilterGroups,
          quickFilters: initialQuickFilters,
          query: ""
        });
      }
    };
    $[9] = currentView;
    $[10] = currentViewFilters.filterGroups;
    $[11] = currentViewFilters.quickFilters;
    $[12] = hasViews;
    $[13] = initialFilter;
    $[14] = onSetFilters;
    $[15] = onUpdateFilters;
    $[16] = uiModeProps.currentUIMode;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const onClearFilters = t3;
  let t4;
  if ($[18] !== onSetDeletedObjects) {
    t4 = () => {
      onSetDeletedObjects([]);
    };
    $[18] = onSetDeletedObjects;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const onClearDeletedObjects = t4;
  let t5;
  if ($[20] !== onSetDeletedObjects) {
    t5 = objectIdOrIds => {
      if (Array.isArray(objectIdOrIds)) {
        onSetDeletedObjects(prevDeletedObjects => [...prevDeletedObjects, ...objectIdOrIds]);
      } else {
        onSetDeletedObjects(prevDeletedObjects_0 => [...prevDeletedObjects_0, objectIdOrIds]);
      }
    };
    $[20] = onSetDeletedObjects;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const onDeleteObject = t5;
  let t6;
  if ($[22] !== deletedObjects || $[23] !== filters || $[24] !== hasAppliedFilters || $[25] !== hiddenFilter || $[26] !== initialFilter || $[27] !== isFilterFromCustomView || $[28] !== onClearDeletedObjects || $[29] !== onClearFilters || $[30] !== onDeleteObject || $[31] !== onRemovePropertyFilter || $[32] !== onUpdateFilters) {
    t6 = {
      deletedObjects,
      filters,
      hasAppliedFilters,
      hiddenFilter,
      initialFilter,
      isFilterFromCustomView,
      onClearDeletedObjects,
      onClearFilters,
      onDeleteObject,
      onRemovePropertyFilter,
      onResetFilters: onClearFilters,
      onUpdateFilters
    };
    $[22] = deletedObjects;
    $[23] = filters;
    $[24] = hasAppliedFilters;
    $[25] = hiddenFilter;
    $[26] = initialFilter;
    $[27] = isFilterFromCustomView;
    $[28] = onClearDeletedObjects;
    $[29] = onClearFilters;
    $[30] = onDeleteObject;
    $[31] = onRemovePropertyFilter;
    $[32] = onUpdateFilters;
    $[33] = t6;
  } else {
    t6 = $[33];
  }
  const value = t6;
  return value;
}
function _temp(filterGroup) {
  return filterGroup.filters.length > 0;
}
export default useCrmObjectTypeListingFilters;